import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCurrentUserProfile } from '@/services/profileService';
import { ProfileData } from '@/types/supabase';
import { debugLogger } from '@/utils/debug';

interface ProfileContextValue {
  profile: ProfileData | null;
  isLoading: boolean;
  refetchProfile: () => void;
}

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined);

export const PROFILE_QUERY_KEY = ['currentUserProfile'];

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: PROFILE_QUERY_KEY,
    enabled: !loading && !!user?.id,
    queryFn: async () => {
      if (!user?.id) return null;
      debugLogger.log('Fetching current user profile (provider)');
      try {
        return await fetchCurrentUserProfile(user.id);
      } catch (e) {
        debugLogger.error('Profile fetch failed:', e);
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Invalidate when user changes
  useEffect(() => {
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    } else {
      queryClient.removeQueries({ queryKey: PROFILE_QUERY_KEY });
    }
  }, [loading, user?.id, queryClient]);

  const value = useMemo(() => ({
    profile: data ?? null,
    isLoading,
    refetchProfile: () => { void refetch(); }
  }), [data, isLoading, refetch]);

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};

export function useUserProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useUserProfile must be used within ProfileProvider');
  return ctx;
}
